import { type FC, useRef } from 'react'
import { classifyOutput } from './classifyOutput'
import { humanizeVariableName } from './variableLabel'
import styles from './CustomWorkflows.module.scss'
import { FileListOutput, FileOutput, HtmlOutput, JsonOutput, PrimitiveOutput, TextOutput } from './outputRenderers'
import { ChecklistOutput, EmailOutput, KeyValueOutput, MarkdownOutput, TableOutput } from './typedOutputRenderers'
import { copyTextFor, outputKindChip } from './copyableOutput'
import { CopyOutputButton } from './CopyOutputButton'
import { type OutputKind, type OutputRenderer, type OutputTypeHint } from './outputTypes'

const OUTPUT_RENDERERS: Record<OutputKind, OutputRenderer> = {
  html: HtmlOutput,
  file: FileOutput,
  'file-list': FileListOutput,
  text: TextOutput,
  json: JsonOutput,
  number: PrimitiveOutput,
  boolean: PrimitiveOutput,
  empty: PrimitiveOutput,
  email: EmailOutput,
  markdown: MarkdownOutput,
  table: TableOutput,
  keyValue: KeyValueOutput,
  checklist: ChecklistOutput,
}

interface WorkflowOutputFieldProps {
  name: string
  value: unknown
  hint?: OutputTypeHint
}

export const WorkflowOutputField: FC<WorkflowOutputFieldProps> = ({ name, value, hint }) => {
  const bodyRef = useRef<HTMLDivElement>(null)
  const output = classifyOutput(value, hint)
  const Renderer = OUTPUT_RENDERERS[output.kind]
  const copyText = copyTextFor(output)
  const chip = outputKindChip(output)
  return (
    <div className={styles.outputField}>
      <div className='d-flex align-items-center gap-2 flex-wrap mb-1'>
        <span className='paragraph-small medium'>{humanizeVariableName(name)}</span>
        {chip ? (
          <span className='d-inline-flex align-items-center zocks-px-0.75 zocks-py-0.125 rounded-2 paragraph-extra-small medium zocks-tag-slate'>{chip}</span>
        ) : null}
        {copyText === null ? null : (
          <span className='ms-auto'>
            <CopyOutputButton text={copyText} targetRef={bodyRef} />
          </span>
        )}
      </div>
      <div ref={bodyRef}>
        <Renderer output={output} label={name} />
      </div>
    </div>
  )
}
